import React, { useContext, useEffect, useState } from 'react';
import { cloneDeep, omit } from 'lodash';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { UploadResourcesModal } from 'components/organisms/Task';
import {
    AddEditCriterionDialog,
    FilterDrawerInner,
    FormativeSetupDialog,
    MilestoneDrawerInner,
    SubmissionPreview,
} from 'components/organisms/Milestones';
import { ScheduleModal } from 'components/organisms/TaskTemplates';
import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'pages/TaskManager/Milestones/mobile';
import Tablet from 'pages/TaskManager/Milestones/tablet';
import Desktop from 'pages/TaskManager/Milestones/desktop';
import { PageContext } from 'pages/TaskManager/PageProvider';
import courseService from 'services/course';
import { AlertDialog, Drawer } from 'shell/components';
import { SUBMISSION_TYPES, TYPES } from 'utils/constants';
import { getDuplicateTitle, getMilestoneCompletion, getMilestoneStatus } from 'utils/helper';

const countFilters = (filterState) => {
    let count = 0;
    if (filterState.sortKey !== 'none') count++;
    if (filterState.statusFilterKeys.length) count++;
    if (filterState.dueDateFilterKeys.length) count++;
    return count;
};

const Milestones = () => {
    const { auth } = useAuth();
    const {
        courseLoading,
        milestones,
        setMilestones,
        createMilestone,
        updateMilestone,
        deleteMilestone,
        publishMilestone,
        unpublishMilestone,
        taskTemplates,
        setTaskTemplates,
        getUserByAccountId,
    } = useInstructor();
    const { message } = useSnackbar();
    const { course, formatives, setFormatives } = useUser2();
    const { width } = useViewport();
    const {
        confirmationModal,
        setConfirmationModal,
        checkedMilestones: checked,
        setCheckedMilestones: setChecked,
        view,
        setView,
        activeTemplateId,
        setActiveTemplateId,
        templateChanges,
        setTemplateChanges,
        spawnedTasks,
        templateController,
        milestoneDrawer,
        setMilestoneDrawer,
    } = useContext(PageContext);

    const [didFetchCompletion, setDidFetchCompletion] = useState(false);
    const [files, setFiles] = useState({});
    const [sharedUrls, setSharedUrls] = useState({});
    const [changes, setChanges] = useState({});

    const [filterDrawer, setFilterDrawer] = useState({
        isOpen: false,
        sortKey: 'none',
        statusFilterKeys: [],
        dueDateFilterKeys: [],
        searchInput: '',
    });
    const [uploadResources, setUploadResources] = useState({ isOpen: false });
    const [submissionView, setSubmissionView] = useState({
        isOpen: false,
        milestoneId: '',
    });
    const [scheduleModal, setScheduleModal] = useState({ visible: false });

    const closeConfirmationModal = () => {
        setConfirmationModal((current) => ({ ...current, visible: false, onConfirm: () => {}, onCancel: () => {} }));
    };

    const getSortedFilteredMilestones = (milestones) => {
        return milestones
            .filter((milestone) => !milestone.isLocal)
            .filter((milestone) => {
                return milestone.title.toLowerCase().includes(filterDrawer.searchInput.toLowerCase());
            })
            .filter((milestone) => {
                if (!filterDrawer.statusFilterKeys.length) return true;
                return filterDrawer.statusFilterKeys.some((key) => {
                    return getMilestoneStatus(milestone).toLowerCase().includes(key);
                });
            })
            .filter((milestone) => {
                if (!filterDrawer.dueDateFilterKeys.length) return true;
                const today = moment(new Date()).hour(0).minute(0).second(0).millisecond(0);
                const dueDate = moment(milestone.end_on);
                if (filterDrawer.dueDateFilterKeys.includes('today')) {
                    return dueDate.format('D M YYYY') === today.format('D M YYYY');
                }
                if (filterDrawer.dueDateFilterKeys.includes('thisweek')) {
                    const startOfWeek = today.clone().day(0).add(1, 'day');
                    const endOfWeek = today.clone().day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek);
                }
                if (filterDrawer.dueDateFilterKeys.includes('nextweek')) {
                    const startOfNextWeek = today.clone().add(7, 'days').day(0).add(1, 'day');
                    const endOfNextWeek = today.clone().add(7, 'days').day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek);
                }
                if (filterDrawer.dueDateFilterKeys.includes('overdue')) {
                    return dueDate.toDate() < today.toDate();
                }
                return false;
            })
            .sort((a, b) => {
                if (filterDrawer.sortKey === 'none') {
                    return b.created_on - a.created_on;
                }
                if (filterDrawer.sortKey === 'titlea-z') {
                    return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1;
                }
                if (filterDrawer.sortKey === 'titlez-a') {
                    return a.title.toLowerCase() < b.title.toLowerCase() ? 1 : -1;
                }
                if (filterDrawer.sortKey === 'closestdue') {
                    return a.end_on - b.end_on;
                }
                if (filterDrawer.sortKey === 'furthestdue') {
                    return b.end_on - a.end_on;
                }
                return 0;
            });
    };

    const onChangeCheck = (milestoneId) => {
        setChecked((current) => {
            return current.includes(milestoneId)
                ? current.filter((id) => id !== milestoneId)
                : [...current, milestoneId];
        });
    };

    const onChangeCheckAll = (checked) => {
        if (checked) {
            setChecked(milestones.map(({ id }) => id));
        } else {
            setChecked([]);
        }
    };

    const handleClickMilestone = (milestoneId) => {
        setMilestoneDrawer({ isOpen: true, milestoneId });
    };

    const submitResources = (files, link) => {
        const timestamp = new Date().getTime();
        const newResources = [];
        if (files.length) {
            const resource = {
                id: uuid.v4(),
                active: true,
                owner_account_id: auth.owner_account_id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: auth.course_id,
                type: 'file',
                data: files[0],
            };
            newResources.push(resource);
            setFiles((current) => ({
                ...current,
                [resource.id]: { ...resource, type: TYPES.MILESTONE, milestone_id: milestoneDrawer.milestoneId },
            }));
        }
        if (link.name && link.url) {
            const resource = {
                id: uuid.v4(),
                active: true,
                owner_account_id: auth.owner_account_id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: auth.course_id,
                type: 'link',
                url: link.url,
                name: link.name,
            };
            newResources.push(resource);
            setSharedUrls((current) => ({
                ...current,
                [resource.id]: { ...resource, type: TYPES.MILESTONE, milestone_id: milestoneDrawer.milestoneId },
            }));
        }
        const milestone = milestones.find((milestone) => milestone.id === milestoneDrawer.milestoneId);
        if (milestone.isLocal) {
            const newTemplate = cloneDeep(milestone);
            newTemplate.resources = (newTemplate.resources || []).concat(newResources);
            _updateMilestone(milestone.id, newTemplate);
        } else {
            setChanges((current) => {
                return {
                    ...current,
                    resources: [
                        ...(current.resources || []),
                        ...newResources.map((resource) => ({ ...resource, action: 'add' })),
                    ],
                };
            });
        }
        setUploadResources((current) => ({ ...current, isOpen: false }));
    };

    const _createMilestone = () => {
        const milestoneId = uuid.v4();
        const now = new Date().getTime();
        const newMilestone = {
            id: milestoneId,
            active: true,
            created_on: now,
            modified_on: now,
            owner_account_id: auth.owner_account_id,
            course_id: auth.course_id,
            title: '',
            workload: 'low',
            end_on: moment(now).endOf('day').valueOf(),
            description: '',
            submission_type: SUBMISSION_TYPES.FILE,
            published_on: 0,
            isLocal: true,
        };
        setMilestones((current) => [...current, newMilestone]);
        setMilestoneDrawer({ isOpen: true, milestoneId });
    };

    const _updateMilestone = (milestoneId, changes) => {
        const { end_on, ...rest } = changes;
        setMilestones((current) => {
            return current.map((milestone) => {
                return milestone.id === milestoneId
                    ? {
                          ...milestone,
                          ...rest,
                          ...(end_on ? { end_on: moment(end_on).endOf('day').valueOf() } : {}),
                      }
                    : milestone;
            });
        });
    };

    const _deleteMilestone = (milestoneId) => {
        const milestone = milestones.find((milestone) => milestone.id === milestoneId);
        if (!milestone.isLocal) {
            deleteMilestone(milestoneId);
            message.success(
                milestone.published_on > 0 && milestone.published_on < new Date().getTime()
                    ? 'Your milestone was successfully unpublished and deleted.'
                    : 'Your milestone was successfully deleted.'
            );
        }
        setMilestones((current) => current.filter((milestone) => milestone.id !== milestoneId));
        setChanges({});
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const deleteMilestoneWithConfirmation = (milestoneId) => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Milestone and Tasks Associated With This Milestone?',
            content:
                'This action cannot be undone. To delete the milestone only, unlink the tasks associated from the milestone.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                setMilestoneDrawer({ isOpen: false, milestoneId: '' });
                _deleteMilestone(milestoneId);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const deleteMilestoneMulti = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Selected Milestones?',
            content: 'All selected milestones will be unpublished and deleted.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                milestones.filter(({ id }) => checked.includes(id)).forEach(({ id }) => deleteMilestone(id));
                setMilestones((current) => current.filter(({ id }) => !checked.includes(id)));
                message.success('All selected milestones successfully unpublished and deleted.');
                setChecked([]);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const duplicateMilestone = (originalMilestoneId) => {
        const now = new Date().getTime();
        const originalMilestone = milestones.find(({ id }) => id === originalMilestoneId);
        const newMilestone = omit(cloneDeep(originalMilestone), ['_id']);

        newMilestone.id = uuid.v4();
        newMilestone.owner_account_id = auth.owner_account_id;
        newMilestone.created_on = now;
        newMilestone.modified_on = now;
        newMilestone.published_on = 0;
        newMilestone.title = getDuplicateTitle(originalMilestone.title);

        const originalFormative = formatives.find(
            ({ active, milestone_id }) => active && milestone_id === originalMilestoneId
        );

        if (originalFormative) {
            const newFormative = omit(cloneDeep(originalFormative), ['_id']);
            newFormative.id = uuid.v4();
            newFormative.created_on = newMilestone.created_on;
            newFormative.modified_on = newMilestone.modified_on;
            newFormative.owner_account_id = newMilestone.owner_account_id;
            newFormative.milestone_id = newMilestone.id;
            setFormatives((current) => [...current, newFormative]);
            courseService.createCourseFormative(course.id, newFormative);
        }

        setMilestones((current) => [...current, newMilestone]);
        createMilestone(newMilestone);
        message.success('Your milestone was successfully duplicated.');
    };

    const saveMilestone = (milestoneId, changes, shouldNotify) => {
        const milestone = milestones.find((milestone) => milestone.id === milestoneId);
        if (milestone.isLocal) {
            const newTemplate = omit(milestone, ['isLocal']);
            setMilestones((current) => {
                return current.map((milestone) => {
                    return milestone.id === milestoneId ? newTemplate : milestone;
                });
            });
            createMilestone(newTemplate);
            message.success('Draft saved.');
        } else {
            _updateMilestone(milestoneId, changes);
            updateMilestone(milestoneId, changes, shouldNotify);
            setChanges({});
            message.success('Your milestone was saved successfully.');
        }
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const _publishMilestone = async (milestoneId, changes) => {
        const now = new Date().getTime();
        const milestone = milestones.find((milestone) => milestone.id === milestoneId);

        if (milestone.isLocal) {
            const newTemplate = omit({ ...milestone, published_on: now }, ['isLocal']);
            await createMilestone(newTemplate);
            setMilestones((current) => {
                return current.map((milestone) => {
                    return milestone.id === milestoneId ? newTemplate : milestone;
                });
            });
            publishMilestone(milestoneId, {});
        } else {
            _updateMilestone(milestoneId, { ...changes, published_on: now });
            publishMilestone(milestoneId, changes);
            setChanges({});
        }

        const linkedTemplates = taskTemplates.filter((template) => template.milestone_id === milestoneId);
        setTaskTemplates((current) => {
            return current.map((currentTemplate) => {
                if (linkedTemplates.map(({ id }) => id).includes(currentTemplate.id)) {
                    return { ...currentTemplate, published_on: now };
                } else return currentTemplate;
            });
        });

        message.success('Your milestone was successfully published.');
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const publishMilestoneMulti = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Publish Selected Milestones?',
            content: 'All selected milestones and their linked task templates will be published.',
            confirmText: 'Publish',
            onConfirm: () => {
                closeConfirmationModal();
                milestones
                    .filter(({ id }) => checked.includes(id))
                    .filter(({ published_on }) => published_on === 0 || published_on > new Date().getTime())
                    .forEach(({ id }) => _publishMilestone(id, {}));
                message.success('All selected milestones successfully published.');
                setChecked([]);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const _unpublishMilestone = (milestoneId) => {
        _updateMilestone(milestoneId, { published_on: 0 });
        unpublishMilestone(milestoneId);
        setChanges({});

        const linkedTemplates = taskTemplates.filter((template) => template.milestone_id === milestoneId);
        setTaskTemplates((current) => {
            return current.map((currentTemplate) => {
                if (linkedTemplates.map(({ id }) => id).includes(currentTemplate.id)) {
                    return { ...currentTemplate, published_on: 0 };
                } else return currentTemplate;
            });
        });

        message.success('Your milestone was successfully unpublished.');
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const scheduleMilestone = async (milestoneId, changes, timestamp) => {
        const milestone = milestones.find((milestone) => milestone.id === milestoneId);
        if (milestone.isLocal) {
            milestone.published_on = timestamp;
            await createMilestone(omit(milestone, ['isLocal']));
            setMilestones((current) => {
                return current.map((milestone) => {
                    return milestone.id === milestoneId ? omit(milestone, ['isLocal']) : milestone;
                });
            });
        } else {
            _updateMilestone(milestoneId, changes);
            updateMilestone(milestoneId, changes);
            setChanges({});
        }

        message.success(
            `Your milestone has successfully been scheduled to be published on ${moment(
                changes.published_on || milestone.published_on
            ).format('MMMM D[,] YYYY')}.`
        );
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const unscheduleMilestone = (milestoneId) => {
        _updateMilestone(milestoneId, { published_on: 0 });
        updateMilestone(milestoneId, { published_on: 0 });
        setChanges({});
        message.success('Your milestone was successfully unscheduled.');
        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
    };

    const setScheduleTime = async (timestamp) => {
        const milestone = milestones.find((milestone) => milestone.id === milestoneDrawer.milestoneId);
        if (milestone.isLocal) {
            scheduleMilestone(milestone.id, changes, timestamp);
        } else {
            scheduleMilestone(milestone.id, { ...changes, published_on: timestamp });
        }
        setScheduleModal({ visible: false });
    };

    const createTemplate = (milestoneId) => {
        const newTemplate = templateController.create(milestoneId);
        setView('template');
        setActiveTemplateId(newTemplate.id);
    };

    const updateTemplate = (templateId, changes) => {
        templateController.update(templateId, changes);
    };

    const deleteTemplate = (templateId) => {
        templateController.delete(templateId);
        setTemplateChanges({});
        setView('milestone');
        setActiveTemplateId('');
    };

    const saveTemplate = (templateId, changes, shouldNotify) => {
        const isLocal = templateController.save(templateId, changes, shouldNotify);
        if (isLocal) {
            setView('milestone');
            setActiveTemplateId('');
        } else {
            setTemplateChanges({});
        }
    };

    const publishTemplate = async (templateId, changes) => {
        setView('milestone');
        setActiveTemplateId('');
        const isLocal = await templateController.publish(templateId, changes);
        if (!isLocal) setTemplateChanges({});
    };

    const unpublishTemplate = (templateId) => {
        templateController.unpublish(templateId);
        setTemplateChanges({});
    };

    const unscheduleTemplate = (templateId) => {
        templateController.unschedule(templateId);
        setTemplateChanges({});
    };

    const closeDrawer = () => {
        if (view === 'milestone') {
            const milestone = milestones.find((milestone) => milestone.id === milestoneDrawer.milestoneId);
            if (milestone?.isLocal && !confirmationModal.visible) {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Leave Milestone Creation?',
                    content: 'All changes made to this milestone will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
                        _deleteMilestone(milestone.id);
                    },
                    onCancel: closeConfirmationModal,
                });
                return;
            }
            if (!milestone?.isLocal) {
                if (!Boolean(Object.keys(omit(changes, ['published_on'])).length)) {
                    setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
                    setChanges({});
                } else {
                    setConfirmationModal({
                        visible: true,
                        destructive: true,
                        title: 'Discard Changes?',
                        content: 'All changes made to this milestone will not be saved.',
                        confirmText: 'Leave',
                        onConfirm: () => {
                            closeConfirmationModal();
                            setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
                            setChanges({});
                        },
                        onCancel: closeConfirmationModal,
                    });
                }
            }
        } else {
            const template = taskTemplates.find(({ id }) => id === activeTemplateId);
            if (template.isLocal) {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Leave Task Creation?',
                    content: 'All changes made to this task will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setView('milestone');
                        setActiveTemplateId('');
                        deleteTemplate(template.id);
                    },
                    onCancel: closeConfirmationModal,
                });
            } else if (Object.keys(changes).length || Object.keys(templateChanges).length) {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Discard Changes?',
                    content: 'All changes made will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setView('milestone');
                        setActiveTemplateId('');
                        setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
                        setChanges({});
                        setTemplateChanges({});
                    },
                    onCancel: closeConfirmationModal,
                });
            } else {
                setView('milestone');
                setActiveTemplateId('');
                setMilestoneDrawer((current) => ({ ...current, isOpen: false }));
            }
        }
    };

    const closeFilterDrawer = () => {
        setFilterDrawer((current) => ({ ...current, isOpen: false }));
    };

    const props = {
        filterDrawer,
        numFilters: countFilters(filterDrawer),
        setFilterDrawer,

        milestones: getSortedFilteredMilestones(milestones),
        checked,
        onChangeCheck,
        onChangeCheckAll,
        getCompletion: getMilestoneCompletion(spawnedTasks),

        onClickMilestone: handleClickMilestone,
        onClickCreate: _createMilestone,
        onClickPublishMulti: publishMilestoneMulti,
        onClickDeleteMulti: deleteMilestoneMulti,
        onClickViewSubmissions: (milestoneId) => setSubmissionView({ isOpen: true, milestoneId }),
        duplicateMilestone,
        deleteMilestone: deleteMilestoneWithConfirmation,
    };

    const milestoneDrawerProps = {
        open: milestoneDrawer.isOpen,
        onClose: closeDrawer,
    };

    const milestoneDrawerInnerProps = {
        isOpen: milestoneDrawer.isOpen,

        milestone: milestones.find(({ id }) => id === milestoneDrawer.milestoneId),
        files,
        setFiles,
        sharedUrls,
        setSharedUrls,
        changes,
        setChanges,

        updateMilestone: _updateMilestone,
        deleteMilestone: _deleteMilestone,
        saveMilestone,
        publishMilestone: _publishMilestone,
        unpublishMilestone: _unpublishMilestone,
        unscheduleMilestone,

        createTemplate,
        updateTemplate,
        deleteTemplate,
        saveTemplate,
        publishTemplate,
        unpublishTemplate,
        unscheduleTemplate,

        setMilestoneDrawer,
        setUploadResources,
        setScheduleModal,
        setConfirmationModal,
        closeConfirmationModal,
    };

    const filterDrawerProps = {
        anchor: 'left',
        open: filterDrawer.isOpen,
        onClose: closeFilterDrawer,
    };

    const filterDrawerInnerProps = {
        filterDrawer,
        setFilterDrawer,
    };

    const uploadResourcesProps = {
        isOpen: uploadResources.isOpen,
        onClose: () => setUploadResources((current) => ({ ...current, isOpen: false })),
        submitResources,
    };

    const submissionPreviewProps = {
        isOpen: submissionView.isOpen,
        milestoneId: submissionView.milestoneId,
        onClickBack: () => setSubmissionView({ isOpen: false, milestoneId: '' }),
        getUserByAccountId,
        setConfirmationModal,
        closeConfirmationModal,
    };

    const responsiveLayouts = {
        mobile: (
            <Mobile
                {...props}
                setConfirmationModal={setConfirmationModal}
                closeConfirmationModal={closeConfirmationModal}
            />
        ),
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const responsiveMilestoneDrawers = {
        mobile: (
            <CustomDrawer device={'mobile'} {...milestoneDrawerProps}>
                <MilestoneDrawerInner {...milestoneDrawerInnerProps} />
            </CustomDrawer>
        ),
        tablet: (
            <CustomDrawer device={'tablet'} {...milestoneDrawerProps}>
                <MilestoneDrawerInner {...milestoneDrawerInnerProps} />
            </CustomDrawer>
        ),
        desktop: (
            <CustomDrawer device={'desktop'} {...milestoneDrawerProps}>
                <MilestoneDrawerInner {...milestoneDrawerInnerProps} />
            </CustomDrawer>
        ),
    };

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer device={'mobile'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
        tablet: (
            <Drawer device={'tablet'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
        desktop: (
            <Drawer device={'desktop'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
    };

    const responsiveUploadResources = {
        mobile: <UploadResourcesModal device={'mobile'} {...uploadResourcesProps} />,
        tablet: <UploadResourcesModal device={'tablet'} {...uploadResourcesProps} />,
        desktop: <UploadResourcesModal device={'desktop'} {...uploadResourcesProps} />,
    };

    const responsiveSubmissionView = {
        mobile: <SubmissionPreview device={'mobile'} {...submissionPreviewProps} />,
        tablet: <SubmissionPreview device={'tablet'} {...submissionPreviewProps} />,
        desktop: <SubmissionPreview device={'desktop'} {...submissionPreviewProps} />,
    };

    useEffect(() => {
        if (courseLoading) return;
        if (!didFetchCompletion) {
            (async () => {
                await Promise.all(
                    milestones.map(async (milestone) => {
                        const tasks = await courseService.listCourseTasks(
                            milestone.course_id,
                            JSON.stringify({
                                active: true,
                                type: TYPES.MILESTONE,
                                milestone_id: milestone.id,
                            })
                        );
                        tasks.forEach((task) => {
                            spawnedTasks[task.id] = task;
                        });
                    })
                );
                setDidFetchCompletion(true);
            })();
        }
    }, [courseLoading, didFetchCompletion]);

    useEffect(() => {
        if (milestoneDrawer.isOpen && milestoneDrawer.milestoneId) {
            (async () => {
                const milestone = milestones.find((milestone) => milestone.id === milestoneDrawer.milestoneId);
                const files = await courseService.listCourseFiles(
                    milestone.course_id,
                    JSON.stringify({
                        type: TYPES.MILESTONE,
                        milestone_id: milestone.id,
                    })
                );
                const sharedUrls = await courseService.listCourseSharedUrls(
                    milestone.course_id,
                    JSON.stringify({
                        type: TYPES.MILESTONE,
                        milestone_id: milestone.id,
                    })
                );
                setFiles((prev) => files.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), prev));
                setSharedUrls((prev) => sharedUrls.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), prev));
                const tasks = await courseService.listCourseTasks(
                    milestone.course_id,
                    JSON.stringify({
                        active: true,
                        type: TYPES.MILESTONE,
                        milestone_id: milestone.id,
                    })
                );
                tasks.forEach((task) => {
                    spawnedTasks[task.id] = task;
                });
            })();
        }
    }, [milestoneDrawer.isOpen, milestoneDrawer.milestoneId]);

    return (
        <>
            {submissionView.isOpen ? null : withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveMilestoneDrawers)}
            {withResponsiveLayout(width, responsiveFilterDrawers)}
            {withResponsiveLayout(width, responsiveUploadResources)}
            {withResponsiveLayout(width, responsiveSubmissionView)}
            <FormativeSetupDialog />
            <AddEditCriterionDialog />
            <AlertDialog
                message={confirmationModal.title}
                description={confirmationModal.content}
                open={confirmationModal.visible}
                variant={confirmationModal.destructive ? 'destructive' : 'primary'}
                actionButtonText={confirmationModal.confirmText}
                onAction={confirmationModal.onConfirm}
                onCancel={confirmationModal.onCancel}
                onClose={closeConfirmationModal}
            />
            <ScheduleModal
                visible={scheduleModal.visible}
                onSchedule={setScheduleTime}
                onCancel={() => setScheduleModal({ visible: false })}
            />
        </>
    );
};

const CustomDrawer = styled(Drawer)(() => {
    return {
        '& .MuiDrawer-paper > .MuiBox-root': {
            height: 'calc(100% - 64px - 69px)',
        },
    };
});

export default Milestones;
